import { Injectable } from '@angular/core'
import * as FS from '@fullstory/browser'
import { UserPolicy } from '@helios/state-management'
import { ReplaySubject } from 'rxjs'

type FSParam = {
  name: string
  attributes: {
    [key: string]: string | number | undefined
  }
}

/**
 * FullStory adapter for telemetry.
 */
@Injectable({
  providedIn: 'root',
})
export class FullStoryService {
  isEnabled = new ReplaySubject(1)

  private isInitialized = false
  private initFailed = false

  private eventQueue?: FSParam[] = []
  private attrQueue?: FSParam[] = []

  /**
   * Initilizes this FullStory service, but this itself has to wait for SideNav instance of FullStory to complete initialization
   * Returns immediately, because this service will collect any incoming events until init is complete and then send them out.
   *
   * @param userPolicy policies specific to the user that we'd want to attach to their uservars
   */
  init(userPolicy: UserPolicy) {
    //not async/await, because we want to return immediately
    this.waitForFullStoryInit()
      .then(() => {
        FS.setUserVars({
          hasDeveloperAccess: userPolicy.accessDeveloperTools,
        })
        this.isInitialized = true
        this.eventQueue?.forEach(e =>
          this.emitCustomEvent(e.name, e.attributes),
        )
        this.attrQueue?.forEach(a => this.setCustomAttributes(a.attributes))

        this.isEnabled.next(true)
        this.checkAdBlocker()
      })
      .catch(e => {
        console.error(e)
        this.initFailed = true
      })
      .finally(() => {
        this.eventQueue = undefined
        this.attrQueue = undefined
      })
  }

  /**
   * Sidenav is responsible for initializing FullStory, as well as calling identify() with the appropriate user info.
   * We cannot call any FS API functions until that is completed.
   * Since we're decoupled from sidenav, we just poll until FS.initialized() == true
   * (under-the-hood, the global window.FS object sets window._fs_initialized)
   *
   * @returns promise that will be resolved after FullStory is initialized, or rejected after 5 seconds of waiting
   */
  private async waitForFullStoryInit() {
    return new Promise<void>((resolve, reject) => {
      let fsInitCounter = 1
      if (FS.isInitialized()) {
        resolve()
        return
      }
      const interval = setInterval(() => {
        if (fsInitCounter++ > 50) {
          clearInterval(interval)
          reject(new Error('Timed out waiting for FS init')) //if this happens, it could be that sidenav is not initializing FullStory in this environment (like stg)
        }
        if (FS.isInitialized()) {
          clearInterval(interval)
          resolve()
        }
      }, 100) //check every 100ms
    })
  }

  /**
   * https://help.fullstory.com/hc/en-us/articles/360020623274-FS-event-API-Sending-custom-event-data-into-FullStory
   */
  emitCustomEvent(
    eventName: string,
    attributes: {
      [key: string]: string | number | undefined
    },
  ) {
    if (this.initFailed) return
    if (this.isInitialized) FS.event(eventName, attributes)
    else this.eventQueue?.push({ name: eventName, attributes })
  }

  /**
   * https://help.fullstory.com/hc/en-us/articles/1500004101581-FS-setVars-API-Sending-custom-page-data-to-FullStory
   */
  setCustomAttributes(attributes: {
    [key: string]: string | number | undefined
  }) {
    if (this.initFailed) return
    if (this.isInitialized) FS.setVars('page', attributes)
    else this.attrQueue?.push({ name: 'page', attributes })
  }

  /**
   * Full story puts a script tag at the top.
   */
  checkAdBlocker() {
    const fullStoryScripts = document.querySelectorAll(
      'script[src*="fullstory.com"]',
    )
    if (fullStoryScripts.length === 0) {
      return
    }

    const el = fullStoryScripts[0] as unknown as HTMLScriptElement

    el.onerror = e => {
      if (e) {
        this.isEnabled.next(false)
      }
    }
  }
}
